import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, Tag, ArrowRight } from 'lucide-react';
import Header from './components/Header8';
import Footer from './components/Footer8';
import Button from './components/Button';

const Cart = () => {
    const navigate = useNavigate();
    const [couponCode, setCouponCode] = useState('');
    const [appliedCoupon, setAppliedCoupon] = useState(null);

    const [cartItems, setCartItems] = useState([
        {
            id: 1,
            name: 'Minimalist Tote Bag',
            price: 2499,
            quantity: 1,
            size: 'M',
            image: 'https://images.unsplash.com/photo-1590874103328-eac38a683ce7?w=400',
        },
        {
            id: 2,
            name: 'Classic White Shirt',
            price: 1999,
            quantity: 2,
            size: 'L',
            image: 'https://images.unsplash.com/photo-1596755094514-f87e34085b2c?w=400',
        },
        {
            id: 3,
            name: 'Leather Wallet',
            price: 1499,
            quantity: 1,
            size: 'One Size',
            image: 'https://images.unsplash.com/photo-1627123424574-724758594e93?w=400',
        },
    ]);

    const updateQuantity = (id, delta) => {
        setCartItems(items =>
            items.map(item =>
                item.id === id
                    ? { ...item, quantity: Math.max(1, item.quantity + delta) }
                    : item
            )
        );
    };

    const removeItem = (id) => {
        setCartItems(items => items.filter(item => item.id !== id));
    };

    const applyCoupon = () => {
        if (couponCode.toUpperCase() === 'SAVE10') {
            setAppliedCoupon({ code: 'SAVE10', discount: 10 });
        } else {
            alert('Invalid coupon code');
        }
    };

    const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const discount = appliedCoupon ? (subtotal * appliedCoupon.discount) / 100 : 0;
    const shipping = subtotal > 2000 ? 0 : 100;
    const total = subtotal - discount + shipping;

    return (
        <div className="min-h-screen bg-white">
            <Header cartCount={cartItems.length} />

            <div className="container mx-auto px-4 lg:px-8 py-8 lg:py-12">
                <h1 className="text-4xl font-bold text-gray-900 mb-8">Shopping Cart</h1>

                {cartItems.length === 0 ? (
                    <div className="text-center py-16">
                        <div className="text-gray-400 mb-4">
                            <svg className="w-24 h-24 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Your cart is empty</h2>
                        <p className="text-gray-600 mb-6">Add some items to get started</p>
                        <Button variant="primary" onClick={() => navigate('/template8/shop')}>
                            Continue Shopping
                        </Button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Cart Items */}
                        <div className="lg:col-span-2 space-y-4">
                            {cartItems.map(item => (
                                <div key={item.id} className="flex gap-4 p-4 border border-gray-200 rounded-lg">
                                    {/* Image */}
                                    <div className="w-24 h-24 flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden">
                                        <img
                                            src={item.image}
                                            alt={item.name}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>

                                    {/* Details */}
                                    <div className="flex-1">
                                        <h3 className="font-semibold text-gray-900 mb-1">{item.name}</h3>
                                        <p className="text-sm text-gray-600 mb-2">Size: {item.size}</p>
                                        <p className="font-semibold text-gray-900">₹{item.price}</p>
                                    </div>

                                    {/* Quantity Controls */}
                                    <div className="flex flex-col justify-between items-end">
                                        <button
                                            onClick={() => removeItem(item.id)}
                                            className="text-gray-400 hover:text-red-600 transition-colors"
                                        >
                                            <Trash2 size={18} />
                                        </button>

                                        <div className="flex items-center gap-2 border border-gray-300 rounded-lg">
                                            <button
                                                onClick={() => updateQuantity(item.id, -1)}
                                                className="w-8 h-8 flex items-center justify-center hover:bg-gray-100"
                                            >
                                                <Minus size={14} />
                                            </button>
                                            <span className="w-8 text-center font-medium">{item.quantity}</span>
                                            <button
                                                onClick={() => updateQuantity(item.id, 1)}
                                                className="w-8 h-8 flex items-center justify-center hover:bg-gray-100"
                                            >
                                                <Plus size={14} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Order Summary */}
                        <div className="lg:col-span-1">
                            <div className="border border-gray-200 rounded-lg p-6 sticky top-24">
                                <h2 className="text-xl font-bold text-gray-900 mb-6">Order Summary</h2>

                                {/* Coupon Code */}
                                <div className="mb-6">
                                    <label className="block text-sm font-medium text-gray-900 mb-2">
                                        Coupon Code
                                    </label>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={couponCode}
                                            onChange={(e) => setCouponCode(e.target.value)}
                                            placeholder="Enter code"
                                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-gray-900"
                                        />
                                        <Button variant="outline" onClick={applyCoupon}>
                                            <Tag size={16} />
                                        </Button>
                                    </div>
                                    {appliedCoupon && (
                                        <p className="text-sm text-green-600 mt-2">
                                            Coupon "{appliedCoupon.code}" applied! {appliedCoupon.discount}% off
                                        </p>
                                    )}
                                    <p className="text-xs text-gray-500 mt-2">Try: SAVE10 for 10% off</p>
                                </div>

                                {/* Price Breakdown */}
                                <div className="space-y-3 mb-6 pb-6 border-b border-gray-200">
                                    <div className="flex justify-between text-gray-600">
                                        <span>Subtotal</span>
                                        <span>₹{subtotal}</span>
                                    </div>
                                    {appliedCoupon && (
                                        <div className="flex justify-between text-green-600">
                                            <span>Discount ({appliedCoupon.discount}%)</span>
                                            <span>-₹{discount}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between text-gray-600">
                                        <span>Shipping</span>
                                        <span>{shipping === 0 ? 'Free' : `₹${shipping}`}</span>
                                    </div>
                                    {shipping === 0 && (
                                        <p className="text-xs text-gray-500">Free shipping on orders over ₹2000</p>
                                    )}
                                </div>

                                {/* Total */}
                                <div className="flex justify-between text-xl font-bold text-gray-900 mb-6">
                                    <span>Total</span>
                                    <span>₹{total}</span>
                                </div>

                                {/* Checkout Button */}
                                <Button
                                    variant="primary"
                                    size="lg"
                                    fullWidth={true}
                                    onClick={() => navigate('/template8/checkout')}
                                >
                                    Proceed to Checkout
                                    <ArrowRight size={20} className="ml-2" />
                                </Button>

                                <button
                                    onClick={() => navigate('/template8/shop')}
                                    className="w-full text-center text-gray-600 hover:text-gray-900 mt-4 text-sm"
                                >
                                    Continue Shopping
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <Footer />
        </div>
    );
};

export default Cart;
